import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Hex helpers: backend stores colors as "D4AF37" (no '#'); the <input type=color>
// element wants "#D4AF37". We keep storage canonical and convert at the boundary.
const toHash = (v) => (!v ? '#000000' : v.startsWith('#') ? v : `#${v}`);
const fromHash = (v) => (v || '').replace(/^#/, '').toUpperCase();

const ColorRow = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-3 py-2">
    <Label className="w-48 text-right text-sm">{label}</Label>
    <input
      type="color"
      value={toHash(value)}
      onChange={(e) => onChange(fromHash(e.target.value))}
      className="w-14 h-9 rounded border cursor-pointer"
      data-testid={testId}
    />
    <Input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(fromHash(e.target.value))}
      className="w-32 font-mono text-xs"
      placeholder="D4AF37"
    />
  </div>
);

const ToggleRow = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-100">
    <Label className="text-sm cursor-pointer">{label}</Label>
    <Switch checked={!!value} onCheckedChange={onChange} data-testid={testId} />
  </div>
);

const ExcelSettingsManagement = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const isUserEditing = useRef(false);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/excel-settings`, auth());
        setSettings(r.data);
      } catch (e) {
        toast.error('فشل تحميل إعدادات Excel');
      }
      setTimeout(() => { initialLoadDone.current = true; }, 50);
    })();
  }, []);

  const update = (patch) => {
    isUserEditing.current = true;
    setSettings((s) => ({ ...s, ...patch }));
  };

  // Debounced auto-save
  useEffect(() => {
    if (!settings) return;
    if (!initialLoadDone.current || !isUserEditing.current) return;
    const handle = setTimeout(async () => {
      try { await axios.put(`${API}/excel-settings`, settings, auth()); }
      catch (e) { /* silent */ }
    }, 1200);
    return () => clearTimeout(handle);
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/excel-settings`, settings, auth());
      setSettings(r.data);
      isUserEditing.current = false;
      toast.success('✅ تم حفظ إعدادات Excel');
    } catch (e) {
      toast.error(`❌ فشل الحفظ: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('إعادة كل إعدادات Excel إلى القيم الافتراضية؟')) return;
    try {
      const r = await axios.post(`${API}/excel-settings/reset`, {}, auth());
      setSettings(r.data);
      isUserEditing.current = false;
      toast.success('🔄 تم استرجاع الإعدادات الافتراضية');
    } catch (e) {
      toast.error('فشل الاسترجاع');
    }
  };

  if (!settings) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="excel-settings-loading">
        جاري تحميل إعدادات Excel...
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl" data-testid="excel-settings-page">
      {/* Header */}
      <div className="bg-gradient-to-l from-emerald-900 via-emerald-800 to-emerald-900 text-amber-300 p-6 rounded-2xl shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-wide">📊 إعدادات Excel</h1>
            <p className="text-amber-100 mt-1 text-sm">
              تحكّم كامل في تصميم ملفات Excel — الألوان، الخطوط، التخطيط، التذييل
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  if (isUserEditing.current) {
                    await axios.put(`${API}/excel-settings`, settings, auth());
                    isUserEditing.current = false;
                  }
                  const token = localStorage.getItem('token') || '';
                  const url = `${API}/excel-settings/preview?token=${encodeURIComponent(token)}&_=${Date.now()}`;
                  // Same-tab navigation pattern via a hidden <a> with download attr.
                  // Works without popup blocker since it's a direct anchor click.
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `excel_preview_${Date.now()}.xlsx`;
                  a.rel = 'noopener';
                  a.target = '_blank';
                  document.body.appendChild(a);
                  a.click();
                  setTimeout(() => a.remove(), 1500);
                  toast.success('📥 جاري تنزيل ملف المعاينة... تحقق من مجلد التنزيلات');
                } catch (e) {
                  toast.error(`فشل المعاينة: ${e?.response?.data?.detail || e.message}`);
                }
              }}
              data-testid="excel-preview-btn"
              className="bg-amber-300 text-black border-amber-500 hover:bg-amber-200 font-bold"
            >
              👁️ معاينة فورية
            </Button>
            <Button
              variant="outline"
              onClick={resetDefaults}
              data-testid="excel-reset-btn"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              🔄 افتراضي
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="excel-save-btn"
              className="bg-amber-400 text-black hover:bg-amber-300 font-bold"
            >
              {saving ? '💾 جارٍ الحفظ...' : '💾 حفظ التغييرات'}
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="identity" className="w-full">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="identity" data-testid="tab-identity">🏢 الهوية</TabsTrigger>
          <TabsTrigger value="template" data-testid="tab-template">🎨 القالب</TabsTrigger>
          <TabsTrigger value="colors" data-testid="tab-colors">🌈 الألوان</TabsTrigger>
          <TabsTrigger value="layout" data-testid="tab-layout">📐 التخطيط</TabsTrigger>
          <TabsTrigger value="locale" data-testid="tab-locale">🌍 الأرقام والتاريخ</TabsTrigger>
        </TabsList>

        {/* === Identity === */}
        <TabsContent value="identity">
          <Card>
            <CardHeader><CardTitle>🏢 معلومات الوكالة الظاهرة في ملف Excel</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>اسم الوكالة (عربي)</Label>
                  <Input value={settings.agency_name_ar || ''}
                    onChange={(e) => update({ agency_name_ar: e.target.value })}
                    data-testid="excel-agency-ar"
                  />
                </div>
                <div>
                  <Label>اسم الوكالة (لاتيني)</Label>
                  <Input value={settings.agency_name_fr || ''}
                    onChange={(e) => update({ agency_name_fr: e.target.value })}
                    data-testid="excel-agency-fr"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>العنوان</Label>
                  <Input value={settings.address || ''}
                    onChange={(e) => update({ address: e.target.value })} />
                </div>
                <div>
                  <Label>الهاتف</Label>
                  <Input value={settings.phone || ''}
                    onChange={(e) => update({ phone: e.target.value })} />
                </div>
                <div>
                  <Label>البريد الإلكتروني</Label>
                  <Input value={settings.email || ''}
                    onChange={(e) => update({ email: e.target.value })} />
                </div>
                <div>
                  <Label>الموقع الإلكتروني</Label>
                  <Input value={settings.website || ''}
                    onChange={(e) => update({ website: e.target.value })} />
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-sm font-bold mb-2">📝 ملاحظات التذييل</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>تذييل (عربي)</Label>
                    <Textarea rows={2} value={settings.footer_note_ar || ''}
                      onChange={(e) => update({ footer_note_ar: e.target.value })}
                      placeholder="شكراً لتعاملكم مع وكالة صنهاجة سفر"
                    />
                  </div>
                  <div>
                    <Label>تذييل (لاتيني)</Label>
                    <Textarea rows={2} value={settings.footer_note_fr || ''}
                      onChange={(e) => update({ footer_note_fr: e.target.value })}
                      placeholder="Merci pour votre confiance"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Template === */}
        <TabsContent value="template">
          <Card>
            <CardHeader><CardTitle>🎨 قالب Excel وخصائص العرض</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>القالب الافتراضي</Label>
                <Select value={settings.default_template || 'royal_gold'}
                  onValueChange={(v) => update({ default_template: v })}>
                  <SelectTrigger data-testid="excel-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="royal_gold">🏆 Royal Gold — أسود وذهبي فاخر</SelectItem>
                    <SelectItem value="classic">📜 كلاسيكي — أبيض وأسود رسمي</SelectItem>
                    <SelectItem value="minimal">✨ مينيمال — تصميم نظيف</SelectItem>
                    <SelectItem value="colorful">🌈 ملوّن — ألوان حيوية</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">القالب يحدد الألوان الأساسية + الأنماط</p>
              </div>

              <div className="pt-3 border-t space-y-1">
                <h3 className="text-sm font-bold mb-2">📦 الكتل المعروضة</h3>
                <ToggleRow label="عرض رأس الوكالة (الاسم في الأعلى)"
                  value={settings.show_agency_header}
                  onChange={(v) => update({ show_agency_header: v })}
                  testId="excel-show-header" />
                <ToggleRow label="عرض الشعار"
                  value={settings.show_logo}
                  onChange={(v) => update({ show_logo: v })}
                  testId="excel-show-logo" />
                <ToggleRow label="عرض صف الاتصال (هاتف + بريد + عنوان)"
                  value={settings.show_contact_row}
                  onChange={(v) => update({ show_contact_row: v })}
                  testId="excel-show-contact" />
                <ToggleRow label="عرض التذييل (Footer)"
                  value={settings.show_footer}
                  onChange={(v) => update({ show_footer: v })}
                  testId="excel-show-footer" />
                <ToggleRow label="عرض اسم منشئ الوصل"
                  value={settings.show_creator_name}
                  onChange={(v) => update({ show_creator_name: v })} />
                <ToggleRow label="عرض اسم الفرع/الوكالة"
                  value={settings.show_branch_name}
                  onChange={(v) => update({ show_branch_name: v })} />
                <ToggleRow label="عرض بصمة الوثيقة (hash)"
                  value={settings.show_doc_hash}
                  onChange={(v) => update({ show_doc_hash: v })} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === Colors === */}
        <TabsContent value="colors">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>🌈 الألوان الأساسية</CardTitle></CardHeader>
              <CardContent>
                <ColorRow label="اللون الأساسي (Accent)" value={settings.primary_color}
                  onChange={(v) => update({ primary_color: v })} testId="color-primary" />
                <ColorRow label="اللون الثانوي" value={settings.secondary_color}
                  onChange={(v) => update({ secondary_color: v })} />
                <ColorRow label="خلفية رأس الصفحة" value={settings.header_bg}
                  onChange={(v) => update({ header_bg: v })} />
                <ColorRow label="نص رأس الصفحة" value={settings.header_text}
                  onChange={(v) => update({ header_text: v })} />
                <ColorRow label="خلفية رأس الجدول" value={settings.table_header_bg}
                  onChange={(v) => update({ table_header_bg: v })} />
                <ColorRow label="نص رأس الجدول" value={settings.table_header_text}
                  onChange={(v) => update({ table_header_text: v })} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>🎯 ألوان مساعدة</CardTitle></CardHeader>
              <CardContent>
                <ColorRow label="صفوف مُتناوبة (Zebra)" value={settings.row_alt_color}
                  onChange={(v) => update({ row_alt_color: v })} />
                <ColorRow label="لون الحدود" value={settings.border_color}
                  onChange={(v) => update({ border_color: v })} />
                <ColorRow label="لون النص العام" value={settings.text_color}
                  onChange={(v) => update({ text_color: v })} />
                <ColorRow label="لون نص ثانوي" value={settings.muted_color}
                  onChange={(v) => update({ muted_color: v })} />
                <div className="pt-3 mt-3 border-t">
                  <h4 className="text-xs font-bold text-gray-500 mb-2">شارات الحالة</h4>
                  <ColorRow label="✅ مدفوع" value={settings.paid_color}
                    onChange={(v) => update({ paid_color: v })} />
                  <ColorRow label="🟡 جزئي" value={settings.partial_color}
                    onChange={(v) => update({ partial_color: v })} />
                  <ColorRow label="🔴 غير مدفوع" value={settings.unpaid_color}
                    onChange={(v) => update({ unpaid_color: v })} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === Layout === */}
        <TabsContent value="layout">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle>📐 الصفحة والطباعة</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>حجم الورق</Label>
                  <Select value={settings.paper_size || 'A4'}
                    onValueChange={(v) => update({ paper_size: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">A4</SelectItem>
                      <SelectItem value="A5">A5</SelectItem>
                      <SelectItem value="Letter">Letter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>اتجاه الورق</Label>
                  <Select value={settings.orientation || 'portrait'}
                    onValueChange={(v) => update({ orientation: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="portrait">📄 طولي</SelectItem>
                      <SelectItem value="landscape">📊 عرضي</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>الهامش (ملم)</Label>
                  <Input type="number" min={5} max={40}
                    value={settings.margin_mm || 12}
                    onChange={(e) => update({ margin_mm: parseInt(e.target.value, 10) || 12 })}
                  />
                </div>
                <div className="pt-3 border-t">
                  <ToggleRow label="ملاءمة العرض للصفحة (Fit to Width)"
                    value={settings.fit_to_width}
                    onChange={(v) => update({ fit_to_width: v })} />
                  <ToggleRow label="إظهار خطوط الشبكة"
                    value={settings.show_gridlines}
                    onChange={(v) => update({ show_gridlines: v })} />
                  <ToggleRow label="تثبيت صف العناوين (Freeze)"
                    value={settings.freeze_header}
                    onChange={(v) => update({ freeze_header: v })} />
                  <ToggleRow label="صفوف متناوبة Zebra"
                    value={settings.zebra_rows}
                    onChange={(v) => update({ zebra_rows: v })} />
                  <ToggleRow label="اتجاه من اليمين لليسار (RTL)"
                    value={settings.rtl}
                    onChange={(v) => update({ rtl: v })} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>🔤 الخطوط</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>عائلة الخط</Label>
                  <Select value={settings.font_family || 'Cairo'}
                    onValueChange={(v) => update({ font_family: v })}>
                    <SelectTrigger data-testid="excel-font-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cairo">Cairo (افتراضي)</SelectItem>
                      <SelectItem value="Tajawal">Tajawal</SelectItem>
                      <SelectItem value="Amiri">Amiri</SelectItem>
                      <SelectItem value="Arial">Arial</SelectItem>
                      <SelectItem value="Calibri">Calibri</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>حجم النص العام (pt)</Label>
                  <Input type="number" min={7} max={20}
                    value={settings.font_size_pt || 11}
                    onChange={(e) => update({ font_size_pt: parseInt(e.target.value, 10) || 11 })}
                  />
                </div>
                <div>
                  <Label>حجم العنوان الرئيسي (pt)</Label>
                  <Input type="number" min={14} max={40}
                    value={settings.heading_size_pt || 22}
                    onChange={(e) => update({ heading_size_pt: parseInt(e.target.value, 10) || 22 })}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === Locale === */}
        <TabsContent value="locale">
          <Card>
            <CardHeader><CardTitle>🌍 الأرقام والتاريخ والعملة</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>رمز العملة</Label>
                  <Input value={settings.currency_symbol || 'دج'}
                    onChange={(e) => update({ currency_symbol: e.target.value })}
                    data-testid="excel-currency" />
                </div>
                <div>
                  <Label>عدد الخانات بعد الفاصلة</Label>
                  <Input type="number" min={0} max={4}
                    value={settings.decimal_places ?? 0}
                    onChange={(e) => update({ decimal_places: parseInt(e.target.value, 10) || 0 })}
                  />
                </div>
                <div>
                  <Label>صيغة التاريخ</Label>
                  <Select value={settings.date_format || '%d/%m/%Y'}
                    onValueChange={(v) => update({ date_format: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="%d/%m/%Y">DD/MM/YYYY</SelectItem>
                      <SelectItem value="%Y-%m-%d">YYYY-MM-DD</SelectItem>
                      <SelectItem value="%d-%m-%Y">DD-MM-YYYY</SelectItem>
                      <SelectItem value="%d %B %Y">DD Month YYYY</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>اللغة الافتراضية للأرقام</Label>
                  <Select value={settings.language || 'ar'}
                    onValueChange={(v) => update({ language: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ar">عربي (1,2,3)</SelectItem>
                      <SelectItem value="fr">لاتيني (1,2,3)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="pt-3 border-t">
                <ToggleRow label="فاصل الآلاف (1,000 بدل 1000)"
                  value={settings.thousand_separator}
                  onChange={(v) => update({ thousand_separator: v })} />
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-md text-xs text-amber-800">
                💡 هذه الإعدادات تطبَّق على كل ملفات Excel المُنزَّلة من النظام
                (الفواتير، الكشوفات، التقارير...). الحفظ تلقائي بعد ثانية من آخر تعديل.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExcelSettingsManagement;
